import { useContextQuery } from './use-context-query';

/**
 * Permission codes whose scope can narrow which media units a caller sees or
 * can filter by — the base catalog visibility grant and the supplier-details
 * grant (CU-86cbenzvw).
 */
const NARROWING_PERMISSION_CODES = ['media_unit.view', 'supplier.view'] as const;

/**
 * Returns true when the caller holds a genuinely scoped (non-wildcard) grant
 * for a permission that narrows the media-unit result set. Mirrors the
 * server's `hasFullyWildcardRow` check (apps/api/src/media-units/media-units.service.ts):
 * a scope row with `mediaType === null AND ownership === null` matches every
 * context and is NOT a restriction; any other row for that permission is.
 *
 * Used to decide whether the empty state may additionally suggest a permission
 * scope as a possible cause for zero matching records — never naming the
 * specific permission, media type, or supplier withheld (see
 * `resolveEmptyMessage`).
 *
 * A permission the caller holds no scope rows for at all is NOT treated as
 * "narrowed" here — its absence is a hide/deny concern for the affected UI
 * (e.g. the Dostawca filter itself, gated by `useHasPermission('supplier.view')`),
 * not a "some rows might be filtered" caveat for the results the caller does see.
 *
 * Returns false (fail-open — no caveat shown) while context is loading,
 * matching the optimistic-visibility default used elsewhere (useHasPermission,
 * useCanOnUnit default to the permissive outcome while loading).
 */
export function useHasNarrowedVisibility(): boolean {
  const { data } = useContextQuery();
  if (data == null) return false;
  const scopes = data.permissionScopes ?? [];
  return NARROWING_PERMISSION_CODES.some((code) => {
    const rows = scopes.filter((entry) => entry.code === code);
    if (rows.length === 0) return false;
    return !rows.some((entry) => entry.mediaType === null && entry.ownership === null);
  });
}
